/*
 * Copyright (c) Nexign, JSC, 2022-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.28.0
 * @since 1.19.0
 */

import { memo, useRef } from 'react';

import moment, { Moment } from 'moment';
import { useSelector } from 'react-redux';

import { icons } from '@assets/index';
import { DropDownFilter, RangeDatePicker } from '@components/common';
import { EInputKind } from '@components/common/Input/Input';
import { IRangeDatePicker } from '@components/common/RangeDatePicker/RangeDatePicker';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { useTableFilterApi } from '@modules/table/api/index';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';

import { tableMetaFieldSelector, tableMetaUrlSelector } from '../../selectors';
import { useTableFilter } from './hooks/useTableFilter';
import { getRangeDatePickerDateFilterValue } from './utils/rangeDatePickerUtils';

interface ITableFilterRangeDatePicker extends Partial<IRangeDatePicker> {
    name: string;
    hideTimePicker?: boolean;
}

const TableFilterRangeDatePicker = ({ name, hideTimePicker, ...props }: ITableFilterRangeDatePicker) => {
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const calendarRef = useRef<RangeDatePicker>(null);

    /* API */

    const apiFilter = useTableFilterApi();

    const { filters, selectedFilter } = useTableFilter(name);

    const meta = useSelector(tableMetaUrlSelector);

    const field = useSelector(tableMetaFieldSelector(meta, name));

    const firstDayOfMonth = moment().startOf('month');

    const start = selectedFilter?.valueFrom
        ? moment(selectedFilter.valueFrom)
        : moment.max(moment(field?.minDate || undefined), firstDayOfMonth);

    const end = selectedFilter?.valueTo && moment(selectedFilter.valueTo);

    const timeLeftHour = start && parseInt(start.format(EFormatData.OnlyHour));
    const timeLeftMin = start && parseInt(start.format(EFormatData.OnlyMinutes));
    const timeRightHour = end && parseInt(end.format(EFormatData.OnlyHour));
    const timeRightMin = end && parseInt(end.format(EFormatData.OnlyMinutes));

    const isFilterChanged = (leftRange: Moment, rightRange: Moment): boolean =>
        !leftRange.isSame(start) || !rightRange.isSame(end);

    const okButtonHandler = (): void => {
        if (calendarRef.current) {
            calendarRef.current.onApply();
        }
    };

    const calendarRangeHandler = (leftRange: Moment, rightRange: Moment): void => {
        if (isFilterChanged(leftRange, rightRange)) {
            apiFilter.filter({
                ...filters,
                [name]: {
                    valueFrom: leftRange.format(EFormatData.FullDataWithTimeZone),
                    valueTo: rightRange.format(EFormatData.FullDataWithTimeZone),
                },
            });
        }
    };

    return (
        <DropDownFilter
            renderInBody
            showCancelBtn
            widthAuto
            filterButtonLabel={getRangeDatePickerDateFilterValue(start, end)}
            icon={icons.calendarIcon}
            iconOpened={icons.calendarIcon}
            kind={EInputKind.normal}
            translation={translationCommon.dropDown}
            onOkClick={okButtonHandler}
        >
            <RangeDatePicker
                endDate={end || (field?.maxDate ? moment(field.maxDate) : undefined)}
                maxDate={field?.maxDate ? moment(field?.maxDate) : undefined}
                messages={translationCommon.calendar}
                minDate={field?.minDate ? moment(field?.minDate) : undefined}
                ref={calendarRef}
                startDate={start}
                timeLeftHour={timeLeftHour}
                timeLeftMin={timeLeftMin}
                timePicker={hideTimePicker}
                timeRightHour={timeRightHour}
                timeRightMin={timeRightMin}
                onApply={calendarRangeHandler}
                {...props}
            />
        </DropDownFilter>
    );
};

export default memo(TableFilterRangeDatePicker);
